import { getMainAppOrigin } from '../../../utils/appOrigin';

function resolveConfiguredOrigin(raw?: string): string | undefined {
  const value = raw?.trim();
  if (!value) return undefined;
  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value.replace(/\/$/, '');
  }
  return `https://${value.replace(/\/$/, '')}`;
}

/**
 * Host that serves /public assets in generated email HTML.
 * Local dev uses the deployed app host so test emails load preset logos.
 */
export function getEmailAssetOrigin(): string {
  const override =
    resolveConfiguredOrigin(process.env.REACT_APP_EMAIL_ASSET_ORIGIN) ||
    resolveConfiguredOrigin(process.env.REACT_APP_APP_DOMAIN);
  if (override) return override;

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'https://app.scubamail.io';
    }
    return window.location.origin.replace(/\/$/, '');
  }

  return getMainAppOrigin();
}

/**
 * Turn builder image paths into absolute, percent-encoded URLs for email HTML.
 * - Relative /public paths → https://app.scubamail.io/...
 * - Spaces and "&" in filenames → %20, %26 (not HTML entities)
 */
export function resolveEmailImageUrl(
  src?: string | null,
  assetOrigin?: string
): string {
  const trimmed = (src ?? '').trim();
  if (!trimmed) return '';

  if (/^data:/i.test(trimmed) || /^cid:/i.test(trimmed)) {
    return trimmed;
  }

  let absolute = trimmed;
  if (/^\/\//.test(trimmed)) {
    absolute = `https:${trimmed}`;
  } else if (!/^https?:\/\//i.test(trimmed)) {
    const origin = (assetOrigin || getEmailAssetOrigin()).replace(/\/$/, '');
    const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    absolute = `${origin}${path}`;
  }

  return encodeUrlPathSegments(absolute);
}

/** Encode path segments; leave query/hash intact. */
function encodeUrlPathSegments(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.pathname = parsed.pathname
      .split('/')
      .map((segment) => {
        if (!segment) return segment;
        try {
          return encodeURIComponent(decodeURIComponent(segment));
        } catch {
          return encodeURIComponent(segment);
        }
      })
      .join('/');
    return parsed.toString();
  } catch {
    return encodeURI(url);
  }
}

/** Escape only characters that break double-quoted HTML attributes (not URL entities). */
export function escapeHtmlAttr(value: string): string {
  return value.replace(/"/g, '&quot;');
}
